/**
 * Which People list, and which Search, Compare and Add operate on — always on
 * screen (Jose, 2026-09-22: "Action context is always explicit").
 *
 *   - In a list: an "Acting on" strip, `People list «<title>» → Search <id> ·
 *     <job title> (<client>)`, or the sentence saying the list has no search
 *     and Add is unavailable.
 *   - On the contact detail page (no list): a "No list selected" strip and a
 *     picker of the person's lists that opens the chosen one's in-list panel.
 *     No actions there.
 *
 * Built from the person's own appearances (03), so the words are the fields
 * the actions will use: the list's `import_id` and its search's `id_thrive`.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { PersonAppearance, PersonDetail } from "@nu-assist/contracts";
import { Button, SearchableSelect } from "@nu-assist/ui";

import type { ActingOn } from "./PersonActions";
import { type PersonTab, personInListPath } from "./personHandle";

export const NO_SEARCH_LINKED = "This list has no search linked — Add to search is unavailable.";
export const ACTIONS_SCOPE_HINT = "Compare and Add use this list and its search only.";
export const NO_LIST_SELECTED = "Compare and Add run inside a people list and its search.";

/** `Search 4512 · Chief Technology Officer (Northwind Labs)`, or null when the
 *  list has no linked search. */
export function searchContextLabel(appearance: PersonAppearance): string | null {
  const s = appearance.search;
  if (!s) return null;
  const title = s.job_title?.trim() || s.name?.trim();
  const client = s.client_company_name?.trim();
  const head = `Search ${s.id_thrive}`;
  const tail = [title, client ? `(${client})` : null].filter(Boolean).join(" ");
  return tail ? `${head} · ${tail}` : head;
}

/** `<list title> → Search … ` or `<list title> → no search linked`. */
export function listContextLabel(appearance: PersonAppearance): string {
  return `${appearance.list_name} → ${searchContextLabel(appearance) ?? "no search linked"}`;
}

/** The appearance the panel is open over. */
export function currentAppearance(
  person: PersonDetail,
  importId: string | null
): PersonAppearance | undefined {
  if (!importId) return undefined;
  const appearances = person.appearances ?? [];
  return (
    appearances.find((a) => a.import_id === importId) ?? appearances.find((a) => a.is_current_list)
  );
}

/** The in-list panel URL for this person on `importId`. An in-app link, so
 *  it carries NO preview: the tab already holds its mode (sessionStorage), and
 *  a `?preview=2` here would demote a tab on `all`/3/4 on reload. Only Copy
 *  link, which leaves the tab, adds the rung. */
export function personListUrl(importId: string, handle: string, tab: PersonTab): string {
  return personInListPath(importId, handle, tab);
}

function Kicker({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-micro font-semibold uppercase tracking-wide text-accent-ink">
      {children}
    </span>
  );
}

/** In a list: what Compare and Add will act on — the page's own list and
 *  search (see `ActingOn`). */
export function ActingOnStrip({ actingOn }: { actingOn: ActingOn | undefined }) {
  if (!actingOn) return null;
  const { listName, searchLabel, note } = actingOn;
  return (
    <section
      data-testid="person-action-context"
      aria-label="Acting on"
      className="rounded-lg border border-accent bg-accent-muted px-4 py-2"
    >
      <Kicker>Acting on</Kicker>
      <p className="text-body text-text" data-testid="person-action-context-text">
        People list «<span className="font-semibold">{listName}</span>» →{" "}
        {searchLabel ? (
          <span className="font-semibold">{searchLabel}</span>
        ) : (
          <span className="text-text-secondary">no search</span>
        )}
      </p>
      {!searchLabel && note && (
        <p
          className="text-caption text-text-secondary"
          data-testid="person-action-context-no-search"
        >
          {note}
        </p>
      )}
    </section>
  );
}

/** On the contact detail page: no list, so no actions — and the way into one. */
export function NoListSelected({ person, tab }: { person: PersonDetail; tab: PersonTab }) {
  const navigate = useNavigate();
  const appearances = person.appearances ?? [];
  const [picked, setPicked] = useState<string | null>(appearances[0]?.import_id ?? null);
  return (
    <section
      data-testid="person-no-list"
      aria-label="No list selected"
      className="mt-3 rounded-lg border border-border bg-bg px-4 py-3"
    >
      <Kicker>No list selected</Kicker>
      <p className="text-body text-text-secondary">{NO_LIST_SELECTED}</p>
      {appearances.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <SearchableSelect
            className="min-w-0 flex-1"
            options={appearances.map((a) => ({ value: a.import_id, label: listContextLabel(a) }))}
            value={picked}
            onChange={setPicked}
            placeholder="Choose a list"
            searchPlaceholder="Filter lists"
          />
          <Button
            data-testid="person-no-list-open"
            disabled={!picked}
            onClick={() => picked && navigate(personListUrl(picked, person.handle, tab))}
          >
            Open
          </Button>
        </div>
      )}
    </section>
  );
}
